import { dequal as deepEqual } from 'dequal/lite';
import lodashGet from 'lodash/fp/get';
import xor from 'lodash/fp/xor';
import { useState } from 'react';
import type { Host } from '#dn-types/host.ts';
import type { Tag } from '#dn-types/tag.ts';
import { getRestartRequiredSettings } from '#screens/hosts/form/advanced/form-data.ts';
import type { HostFormData, HostFormTransformedData } from '#screens/hosts/form/host-form-schema.ts';

type FormData = Pick<HostFormData, 'tags' | 'configOverrides' | 'listenPort'>;

type FormTransformedData = Pick<HostFormTransformedData, 'tags' | 'configOverrides' | 'listenPort'>;

export function useRestartRequired() {
  // The platform rides along with the id so the modal can name the right thing to restart: the
  // definednet.dns.* keys only ever apply to mobile, where there is no dnclient to run commands on.
  const [restartHost, setRestartHost] = useState<{ id: string; platform: Host['metadata']['platform'] } | null>(null);

  return { restartHost, setRestartHost, checkRestartRequired };
}

/**
 * Does an added/removed tag carry a config override that needs a restart?
 */
export function checkTagsRestartRequired(
  newTags: string[],
  originalTags: string[],
  allTags: Tag[],
  hostMeta: Host['metadata']
) {
  const restartRequiredSettings = getRestartRequiredSettings(hostMeta);
  // Get symmetric difference between new and old tags
  const changedTagNames = xor(newTags, originalTags);
  // Filter down to the tags that contain RESTART_REQUIRED config overrides
  return changedTagNames.some((updatedTagName) => {
    const updatedTag = allTags.find((t) => updatedTagName === t.name);
    return restartRequiredSettings.some((key) =>
      updatedTag?.configOverrides.some((configOverride) => key === configOverride.key)
    );
  });
}

/**
 * Have we changed a config override that needs a restart?
 */
export function checkRestartRequired(
  formValues: FormTransformedData,
  originalFormValues: FormData,
  allTags: Tag[],
  hostMeta: Host['metadata']
) {
  const restartRequiredSettings = getRestartRequiredSettings(hostMeta);
  const updatedTagsRestartRequired = checkTagsRestartRequired(
    formValues.tags,
    originalFormValues.tags,
    allTags,
    hostMeta
  );
  // Check if listenPort changed
  const updatedListenPort = formValues.listenPort !== originalFormValues.listenPort;

  return (
    updatedTagsRestartRequired ||
    updatedListenPort ||
    restartRequiredSettings.some((key) => {
      return !deepEqual(lodashGet(key, formValues.configOverrides), lodashGet(key, originalFormValues.configOverrides));
    })
  );
}
